import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Drawer,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { DataTable } from "../../components/DataTable/DataTable";
import { useCollection } from "../../hooks/useCollection";
import { tokens } from "../../utils/figmaTokens";
import {
  getPendingTeachers,
  reviewVerification,
} from "../../services/dataService";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission, permissions } from "../../config/permissions";
import { logUserActivity } from "../../utils/activityLogger";
import {
  toVerificationRow,
  approvalRate,
} from "../../utils/verificationPresentation";

const MAX_REASON = 1000;
const actionButtonSx = { padding: "8px 32px", fontSize: 15, fontWeight: 500 };

const Verification = () => {
  const { user } = useAuth();
  const userRole = user?.role || "";
  const canReview = hasPermission(userRole, permissions.REVIEW_VERIFICATION);

  // Memoized: an inline fetcher is a new identity every render and would
  // make useCollection's effect refetch forever.
  const fetcher = useCallback(() => getPendingTeachers(), []);
  const { data, loading, error, refetch } = useCollection(fetcher);
  const rows = useMemo(() => data.map(toVerificationRow), [data]);

  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");
  // 026 D22, 047 D89: approval asks for the hourly rate, prefilled from the tutor's own rate, else E£150.
  const [approveOpen, setApproveOpen] = useState(false);
  const [rate, setRate] = useState("");

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const notify = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });
  const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  const openDrawer = (row) => {
    setSelected(row);
    setDrawerOpen(true);
  };
  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelected(null);
    setRejectOpen(false);
    setReason("");
    setApproveOpen(false);
    setRate("");
  };

  // /verification?teacher=<id> — a queue row opens that tutor's drawer. A tutor
  // reviewed since the queue was drawn is simply not pending any more.
  const [searchParams, setSearchParams] = useSearchParams();
  const wantedTeacherId = searchParams.get("teacher");
  useEffect(() => {
    if (!wantedTeacherId || loading) return;
    const row = rows.find((r) => r.id === wantedTeacherId);
    if (row) openDrawer(row);
    else notify("That teacher is no longer awaiting verification.", "info");
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantedTeacherId, loading, rows]);

  const openApprove = () => {
    setRate(approvalRate(selected));
    setApproveOpen(true);
  };

  const decide = async (decision, decisionReason = "", salary) => {
    if (!selected) return;
    const userId = user?.uid || user?.id || "unknown";
    const userName = user?.name || user?.displayName || "Unknown User";
    const userEmail = user?.email || "unknown@example.com";
    setDeciding(true);
    try {
      await reviewVerification({
        teacherId: selected.id,
        decision,
        reason: decisionReason,
        ...(decision === "approved" ? { salary } : {}),
      });
      await logUserActivity({
        userId,
        userName,
        userEmail,
        // UPPER_SNAKE: the Activity Log only prettifies actions in that shape.
        action: `VERIFICATION_${decision.toUpperCase()}`,
        entityType: "teacher",
        entityId: selected.id,
        entityName: selected.name,
        before: { verifyStatus: "pending" },
        after: {
          verifyStatus: decision,
          verifyReason: decision === "rejected" ? decisionReason.trim() : "",
          ...(decision === "approved" ? { salary } : {}),
        },
        description: `${userName} ${
          decision === "approved" ? "approved" : "rejected"
        } ${selected.name}'s verification`,
      });
      notify(`${decision === "approved" ? "Approved" : "Rejected"} ${selected.name}.`);
      closeDrawer();
      await refetch();
    } catch (e) {
      notify(
        e.message ||
          `Failed to ${decision === "approved" ? "approve" : "reject"}.`,
        "error",
      );
    } finally {
      setDeciding(false);
    }
  };

  const trimmedReasonLength = reason.trim().length;
  const canSubmitReject =
    trimmedReasonLength > 0 && trimmedReasonLength <= MAX_REASON && !deciding;
  const rateNumber = Number(rate);
  const canSubmitApprove =
    rate.trim() !== "" && Number.isFinite(rateNumber) && rateNumber > 0 && !deciding;

  const columns = [
    { key: "name", label: "Name", sortable: true, render: (row) => row.name },
    {
      key: "subjects",
      label: "Subjects",
      render: (row) => (row.subjects.length ? row.subjects.join(", ") : "-"),
    },
    {
      key: "contact",
      label: "Contact",
      render: (row) => row.email || row.phone || "-",
    },
  ];

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: "bold", mt: 1 }}>
          Verification
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Teachers awaiting review.
        </Typography>
      </Box>

      <DataTable
        columns={columns}
        rows={rows}
        searchKeys={["name", "email", "phone"]}
        initialSort={{ field: "name", direction: "asc" }}
        onRowClick={openDrawer}
        loading={loading}
        error={error}
        emptyText="No teachers awaiting verification"
      />

      <Drawer anchor="right" open={drawerOpen} onClose={closeDrawer}>
        <Box sx={{ width: { xs: 320, sm: 420 }, p: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {selected?.name || "Teacher"}
            </Typography>
            <IconButton onClick={closeDrawer}>
              <CloseIcon />
            </IconButton>
          </Box>

          {selected && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Subjects
                </Typography>
                <Typography variant="body2">
                  {selected.subjects.length ? selected.subjects.join(", ") : "-"}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Bio
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {selected.bio || "-"}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1 }}
              >
                Certificates
              </Typography>
              {selected.certificates.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No certificates uploaded.
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2 }}>
                  {selected.certificates.map((cert, i) => (
                    <Box
                      key={cert.id || i}
                      sx={{
                        width: 140,
                        border: `1px solid ${tokens.border.default}`,
                        borderRadius: "8px",
                        p: 1,
                      }}
                    >
                      {cert.fileUrl ? (
                        <a href={cert.fileUrl} target="_blank" rel="noopener noreferrer">
                          <Box
                            component="img"
                            src={cert.fileUrl}
                            alt={cert.name || "Certificate"}
                            sx={{
                              width: "100%",
                              height: 90,
                              objectFit: "cover",
                              borderRadius: "4px",
                              display: "block",
                            }}
                          />
                        </a>
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            height: 90,
                            bgcolor: "action.hover",
                            borderRadius: "4px",
                          }}
                        />
                      )}
                      <Typography
                        variant="caption"
                        sx={{ display: "block", mt: 0.5, fontWeight: 500 }}
                        noWrap
                      >
                        {cert.name || "Untitled"}
                      </Typography>
                      {cert.organization && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          sx={{ display: "block" }}
                        >
                          {cert.organization}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )}

              {canReview && (
                <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                  <Button
                    variant="contained"
                    color="success"
                    disabled={deciding}
                    onClick={openApprove}
                    sx={actionButtonSx}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    disabled={deciding}
                    onClick={() => setRejectOpen(true)}
                    sx={actionButtonSx}
                  >
                    Reject
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </Drawer>

      <Dialog
        open={rejectOpen}
        onClose={() => !deciding && setRejectOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "8px" } }}
      >
        <DialogTitle style={{ fontWeight: 600 }}>
          Reject {selected?.name || "teacher"}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Reason"
            fullWidth
            multiline
            rows={4}
            margin="normal"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            helperText={`${trimmedReasonLength}/${MAX_REASON}`}
            error={trimmedReasonLength > MAX_REASON}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRejectOpen(false)}
            disabled={deciding}
            sx={actionButtonSx}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={!canSubmitReject}
            onClick={() => decide("rejected", reason)}
            sx={actionButtonSx}
          >
            {deciding ? "Rejecting..." : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={approveOpen}
        onClose={() => !deciding && setApproveOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "8px" } }}
      >
        <DialogTitle style={{ fontWeight: 600 }}>
          Approve {selected?.name || "teacher"}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Hourly rate (E£ per taught hour)"
            type="number"
            fullWidth
            margin="normal"
            autoFocus
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            inputProps={{ min: 0, step: 0.5 }}
            helperText="What the tutor earns per taught hour — a 25-minute lesson pays half. Required: approval writes it with the status."
            error={rate.trim() !== "" && !(Number.isFinite(rateNumber) && rateNumber > 0)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setApproveOpen(false)}
            disabled={deciding}
            sx={actionButtonSx}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={!canSubmitApprove}
            onClick={() => decide("approved", "", rateNumber)}
            sx={actionButtonSx}
          >
            {deciding ? "Approving..." : "Approve"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={closeSnackbar}>
        <Alert severity={snackbar.severity} onClose={closeSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Verification;
